import React, { MouseEventHandler } from 'react';

import { StandardEventProperties, track } from '@betterment/js-runtime';

import Link, { Props as LinkProps, StyledReactRouterLink } from './Link';

import { useRetailLink } from '../../RetailApp/components/LinkProvider';
import { isUrlAllowed } from '../path-to-regexp';

export type TrackableLinkProps<P = LinkProps> = {
  children?: React.ReactNode | ((trackingProps: { onClick: MouseEventHandler }) => React.ReactNode);
  component?: React.ComponentType<React.PropsWithChildren<P>>;
  eventProperties: StandardEventProperties;
  onClick?: MouseEventHandler;
} & P;

function TrackableLink<P = LinkProps>(props: TrackableLinkProps<P>) {
  const { eventProperties, onClick, children, ...rest } = props;
  const { href } = props as LinkProps;
  const retailLink = useRetailLink();
  const hrefWithoutBasename = stripBasename(href || '', '/app') || '';

  const isAllowedUrl = href && isUrlAllowed(retailLink?.allowedUrls, hrefWithoutBasename);

  if (retailLink?.link === 'pangea' && isAllowedUrl) {
    const trackClick: MouseEventHandler = (event: React.MouseEvent) => {
      event.stopPropagation();
      track('ElementClicked', eventProperties);
      if (onClick) {
        onClick(event);
      }
    };

    if (typeof children === 'function') {
      return <>{children({ onClick: trackClick })}</>;
    }

    return (
      <StyledReactRouterLink
        to={hrefWithoutBasename}
        onClick={trackClick}
        eventproperties={eventProperties}
        {...rest}
        data-turbolinks="false"
      >
        {children}
      </StyledReactRouterLink>
    );
  }

  return <TrackableLinkLegacy {...props} />;
}

function TrackableLinkLegacy<P = LinkProps>({
  component: Component,
  eventProperties,
  onClick,
  children,
  ...props
}: TrackableLinkProps<P>) {
  const trackClick: MouseEventHandler = (event: React.MouseEvent) => {
    track('ElementClicked', eventProperties);
    if (onClick) {
      onClick(event);
    }
  };

  if (typeof children === 'function') {
    return <>{children({ onClick: trackClick })}</>;
  }

  if (Component) {
    return (
      <Component {...(props as P)} onClick={trackClick}>
        {children}
      </Component>
    );
  }

  return (
    <Link {...props} onClick={trackClick}>
      {children}
    </Link>
  );
}

// copied from https://github.com/remix-run/react-router/blob/0f2b167e9b862d5e6b3425438787c8e7b39197f4/packages/router/utils.ts#L1099
function stripBasename(pathname: string, basename: string): string | null {
  if (basename === '/') return pathname;

  if (!pathname?.toLowerCase().startsWith(basename.toLowerCase())) {
    return null;
  }

  // We want to leave trailing slash behavior in the user's control, so if they
  // specify a basename with a trailing slash, we should support it
  const startIndex = basename.endsWith('/') ? basename.length - 1 : basename.length;
  const nextChar = pathname.charAt(startIndex);
  if (nextChar && nextChar !== '/') {
    // pathname does not start with basename/
    return null;
  }

  return pathname.slice(startIndex) || '/';
}

export default TrackableLink;
